/**
 * Rendered height of each logical line (`\n`-separated) in a code editable.
 * Soft-wrapped lines return the full stacked height so the gutter can match
 * (Sublime / VS Code style); empty lines use the computed line-height.
 */
export const measureLogicalLineHeights = (root) => {
  if (!root) return [];

  const text = root.textContent || '';
  const spans = [];
  let start = 0;
  for (let i = 0; i <= text.length; i += 1) {
    if (i === text.length || text[i] === '\n') {
      spans.push({ start, end: i });
      start = i + 1;
    }
  }

  const nodes = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node) {
    nodes.push(node);
    node = walker.nextNode();
  }

  const locate = (offset) => {
    let remaining = offset;
    for (let i = 0; i < nodes.length; i += 1) {
      const n = nodes[i];
      const len = n.data.length;
      if (remaining <= len) return { node: n, offset: remaining };
      remaining -= len;
    }
    if (nodes.length === 0) return null;
    const last = nodes[nodes.length - 1];
    return { node: last, offset: last.data.length };
  };

  const style = window.getComputedStyle(root);
  const parsedLh = parseFloat(style.lineHeight);
  const fontSize = parseFloat(style.fontSize) || 12;
  const lineHeight = Number.isFinite(parsedLh) && parsedLh > 0 ? parsedLh : fontSize * 1.55;

  return spans.map(({ start: a, end: b }) => {
    if (a === b || nodes.length === 0) return lineHeight;
    const from = locate(a);
    const to = locate(b);
    if (!from || !to) return lineHeight;
    try {
      const range = document.createRange();
      range.setStart(from.node, from.offset);
      range.setEnd(to.node, to.offset);
      const rects = range.getClientRects();
      let top = Infinity;
      let bottom = -Infinity;
      for (let i = 0; i < rects.length; i += 1) {
        const r = rects[i];
        if (r.height <= 0) continue;
        if (r.top < top) top = r.top;
        if (r.bottom > bottom) bottom = r.bottom;
      }
      if (!Number.isFinite(top) || bottom <= top) return lineHeight;
      return bottom - top;
    } catch {
      return lineHeight;
    }
  });
};
